import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  PaymentRequestButtonElement,
  useStripe,
} from "@stripe/react-stripe-js";
import { ExpressCheckoutModalProps } from "../../../helpers/types.ts";
import {
  expressSelf,
  expressGift,
  handleSubscribe,
} from "../../../components/newCheckout/services/stripe-helpers.js";
import { useDispatch } from "react-redux";
import {
  setPageStatus,
  setPurchaseResponse,
} from "../../../components/newCheckout/redux/slices/navSlice.js";
import validator from "validator";
import { InKindNetworkError } from "../../../helpers/InKindNetworkError.js";
import { ReactReduxContext } from "react-redux";
import { handleAmexRedeemed } from "../../../helpers/handleAmexLoad.js";
import InputLayout from "../../molecules/InputLayout.tsx";
import { useForm, Controller } from "react-hook-form";
import Or from "../../../components/newCheckout/components/Or.jsx";
import { IRootState } from "../../../components/newCheckout/redux/store.js";
import { CheckboxProps } from "../../../helpers/types.ts";
import useDeviceId from "../../../components/events/useDeviceId.tsx";
import useSessionId from "../../../components/events/useSessionId.tsx";

declare global {
  interface Window {
    _ik_on_purchase: any;
  }
} //This helps by extending the global Window interface

const Checkbox = ({ control, onChange, ...props }: CheckboxProps) => {
  return (
    <input
      {...props}
      {...control}
      type="checkbox"
      onChange={(event) => {
        control.onChange(event);
        if (onChange) {
          onChange(event.target.checked);
        }
      }}
    />
  );
};

const handleOnPurchase = (response, type) => {
  let gifted;
  if (window._ik_on_purchase != null) {
    type === "gift" ? (gifted = response.credit_to_give) : (gifted = 0.0);
    window._ik_on_purchase(
      response.project_slug,
      "gco",
      response.id,
      response.amount_to_charge,
      response.credit_to_give,
      gifted,
    );
  }
};

const ExpressCheckoutModal = ({
  shouldDisplay,
  setShowModal,
  subscriptionNum,
  setButtonType,
  buttonType,
  paymentRequest,
  setPaymentRequest,
  displayButtonOnly,
  standAlone,
}: ExpressCheckoutModalProps) => {
  const {
    register,
    watch,
    setFocus,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm();
  const deviceId = useDeviceId();
  const sessionId = useSessionId();
  const stripe = useStripe();
  const [isAgreeAllChecked, setIsAgreeAllChecked] = useState(false);
  const [expressError, setExpressError] = useState<null | string>(null);
  const presenter = useSelector((state: IRootState) => state.company.presenter);
  const useAmount = !!presenter?.subtotal
    ? presenter.subtotal
    : presenter.pay_amount;
  const is_inkind_cash = presenter.is_inkind_cash;
  const isSubscription = useSelector(
    (state: IRootState) => state.nav.subscription,
  );
  const curSubscriptionTier = useSelector(
    (state: IRootState) => state.nav.curSubscriptionTier,
  );
  const watchCheckboxes = watch(["smsAgreed", "emailAgreed"]);
  const [isAmex, setIsAmex] = React.useState(
    window.location.pathname.split("/")[1] === "amex-business-special" ||
      new URLSearchParams(window.location.search).get("utm_campaign") ===
        "amex-business-special",
  );
  let subAmount =
    !!isSubscription && !!curSubscriptionTier?.amount_to_charge
      ? curSubscriptionTier.amount_to_charge
      : 0;

  React.useEffect(() => {
    const val = watchCheckboxes.every((check) => check);
    setValue("agreeAll", val);
    setIsAgreeAllChecked(val);
  }, watchCheckboxes);

  React.useEffect(() => {
    setPaymentRequest(null);
    if (!!isSubscription && subAmount === 0) {
      return;
    }
    if (!!stripe) {
      const pr = stripe.paymentRequest({
        country: presenter.data_country,
        currency: presenter.data_currency,
        total: {
          label: "Account Credit",
          amount: !!isSubscription
            ? Math.round(Number(subAmount) * 100)
            : Math.round(Number(useAmount) * 100),
        },
        requestPayerName: true,
        requestPayerEmail: true,
        requestPayerPhone: true,
      });
      pr.canMakePayment().then((result) => {
        if (result) {
          setPaymentRequest(pr);
        }
      });
    }
  }, [stripe, subscriptionNum]);

  const dispatch = useDispatch();
  const user = useSelector((state: IRootState) => state.user);
  const nav = useSelector((state: IRootState) => state.nav);
  const ikPass = useSelector((state: IRootState) => state.nav.ikPass);
  const ikPassSingle = useSelector(
    (state: IRootState) => state.nav.ikPassSingle,
  );
  const recip = useSelector((state: IRootState) => state.recip);
  const gift_card_option_id = presenter.gift_card_option_id;
  const idToUse =
    presenter.inkind_cash_offer_id ?? presenter.gift_card_option_id;
  const inkind_cash_offer_id = presenter.inkind_cash_offer_id;
  let userToken = user.user && JSON.parse(user.user).token;
  const isUS = presenter.data_country === "US";
  const requireCheckboxes = () => {
    return isUS && !isAgreeAllChecked;
  };

  React.useEffect(() => {
    if (!paymentRequest) {
      return;
    }
    setButtonType(paymentRequest._buttonTypeName);
    paymentRequest?.on("token", function (event) {
      const cardBrand = event?.token?.card?.brand;
      const useFormDetails =
        !!user.newUser &&
        !!user.newEmail &&
        !!user.newPhone &&
        !!user.newFirstName &&
        !!user.newLastName &&
        validator.isEmail(user.newEmail);
      // event.token is available

      if (!!isSubscription) {
        handleSubscribe(
          event.token.id,
          null,
          useFormDetails ? user.newEmail : event.payerEmail,
          userToken,
          nav.utm,
          { "X-InKind-Session": sessionId, "X-Inkind-Device": deviceId },
        )
          .then((response) => {
            dispatch(setPurchaseResponse(response.data));
            event.complete("success");
            dispatch(setPageStatus("confirmation"));
          })
          .catch((error) => {
            if (error?.response) {
              const err = InKindNetworkError(error);
              if (err?.message) {
                setExpressError(err?.message);
              }
            }
            event.complete("fail");
          });
      } else if (!nav.gift) {
        expressSelf(
          event.token.id,
          useFormDetails ? user.newEmail : event.payerEmail,
          idToUse ?? presenter.id,
          useFormDetails ? user.newPhone : event.payerPhone,
          useFormDetails
            ? user.newFirstName
            : event.payerName.split(" ")[0] || event.payerName,
          useFormDetails
            ? user.newLastName
            : event.payerName.split(" ").slice(1).slice(-1).join(" ") || "",
          nav.utm,
          userToken,
          ikPass,
          ikPassSingle,
          user?.subscription?.id ?? null,
          presenter.data_country,
          isAgreeAllChecked,
          { "X-InKind-Session": sessionId, "X-Inkind-Device": deviceId },
        )
          .then((response) => {
            !!isAmex ? handleAmexRedeemed(response.data, cardBrand) : null;

            !!ikPass
              ? handleOnPurchase(
                  response?.data?.subscription_purchase ??
                    response.data.subscription_update,
                  "self",
                )
              : handleOnPurchase(
                  response.data?.purchase ?? response.data?.transaction,
                  "self",
                );
            !!ikPass
              ? dispatch(
                  setPurchaseResponse(
                    response?.data?.subscription_purchase ??
                      response.data.subscription_update,
                  ),
                )
              : dispatch(setPurchaseResponse(response.data));

            event.complete("success");
            dispatch(setPageStatus("confirmation"));
          })
          .catch((error) => {
            if (error?.response) {
              const err = InKindNetworkError(error);
              if (err?.message) {
                setExpressError(err?.message);
              }
            }
            event.complete("fail");
          });
      } else {
        expressGift(
          event.token.id,
          idToUse,
          useFormDetails ? user.newPhone : event.payerPhone,
          useFormDetails
            ? user.newFirstName
            : event.payerName.split(" ")[0] || event.payerName,
          useFormDetails
            ? user.newLastName
            : event.payerName.split(" ").slice(1).slice(-1).join(" ") || "",
          useFormDetails ? user.newEmail : event.payerEmail,
          !!recip.sendersName
            ? recip.sendersName
            : useFormDetails
              ? user.newFirstName
              : event.payerName.split(" ")[0] || event.payerName,
          recip.recipientName,
          recip.recipientEmail,
          recip.giftMessage,
          recip.deliveryDate,
          nav.emailMeGift,
          nav.bonusMeGift,
          nav.utm,
          is_inkind_cash,
          inkind_cash_offer_id ?? null,
          userToken,
          ikPassSingle,
          { "X-InKind-Session": sessionId, "X-Inkind-Device": deviceId },
        )
          .then((response) => {
            !!isAmex ? handleAmexRedeemed(response.data, cardBrand) : null;

            handleOnPurchase(
              response.data.purchase ?? response.data.transaction,
              "gift",
            );
            dispatch(setPurchaseResponse(response.data));
            event.complete("success");
            dispatch(setPageStatus("confirmation"));
          })
          .catch((error) => {
            if (error?.response) {
              const err = InKindNetworkError(error);
              if (err?.message) {
                setExpressError(err?.message);
              }
            }
            event.complete("fail");
          });
      }
    });
  }, [paymentRequest, subscriptionNum]);

  if (paymentRequest) {
    const options = {
      paymentRequest,
      style: {
        paymentRequestButton: {
          height: "50px",
        },
      },
    };

    return (
      <div
        className={
          !displayButtonOnly ? "checkout-overlay" : "express-button-section"
        }
        id="checkout-modal-outer"
        style={{
          display: !!displayButtonOnly
            ? "block"
            : !!shouldDisplay
              ? "flex"
              : "none",
        }}
        onClick={(e) =>
          e.target === e.currentTarget ? setShowModal(false) : null
        }
      >
        <div className="checkout-modal-container express-col">
          {!displayButtonOnly ? (
            <>
              <div className="mod-header">
                <h5>
                  Checkout with{" "}
                  {buttonType === "GOOGLE_PAY"
                    ? "Google Pay"
                    : buttonType === "APPLE_PAY"
                      ? "Apple Pay"
                      : buttonType === "LINK"
                        ? "Link"
                        : "Express"}
                </h5>
                <div
                  className="close-icon"
                  onClick={() => setShowModal(false)}
                ></div>
              </div>
              <form onSubmit={() => {}} className="form-col-account">
                <InputLayout overrideClass={"sms"}>
                  <div
                    className="check-box-checkout"
                    style={{ alignItems: "start" }}
                  >
                    <Checkbox
                      id="sms-checkbox"
                      type="checkbox"
                      defaultValue={user.smsAgreed}
                      control={register("smsAgreed", { required: true })}
                      aria-label="sms-checkbox"
                      className={`check-box-checkout ${
                        errors.smsAgreed
                          ? "field floating-label-wrapper outline error-outline"
                          : ""
                      }`}
                    />
                    <label htmlFor="sms-checkbox">
                      I agree to receive text messages from inKind, including
                      verification and marketing messages at the phone number
                      provided. Reply STOP to cancel. Message and data rates may
                      apply.
                    </label>
                  </div>
                </InputLayout>

                <InputLayout overrideClass={"email"} alignmentOverride={true}>
                  <div
                    className="check-box-checkout terms"
                    style={{ alignItems: "start" }}
                  >
                    <Checkbox
                      id="email-checkbox"
                      type="checkbox"
                      defaultValue={user.emailAgreed}
                      control={register("emailAgreed", { required: true })}
                      aria-label="email-checkbox"
                      className={`check-box-checkout ${
                        errors.emailAgreed
                          ? "field floating-label-wrapper outline error-outline"
                          : ""
                      }`}
                      style={{ marginTop: "6px" }}
                    />
                    <label htmlFor="email-checkbox">
                      I agree to inKind’s{" "}
                      <a
                        href={"https://inkind.com/terms"}
                        className="linker"
                        target="_blank"
                      >
                        Terms of Use
                      </a>
                      ,{" "}
                      <a
                        href={"https://inkind.com/privacy"}
                        className="linker"
                        target="_blank"
                      >
                        Privacy Policy
                      </a>{" "}
                      and{" "}
                      <a
                        href={"https://inkind.com/notice-financial-incentive"}
                        className="linker"
                        target="_blank"
                      >
                        Notice of Financial Incentive
                      </a>
                      , which includes receiving marketing communications from
                      inKind.
                    </label>
                  </div>
                </InputLayout>

                <InputLayout
                  error={
                    errors.agreeAll?.type === "required" &&
                    "You must agree to all to continue"
                  }
                  overrideClass={"check-box-checkout"}
                >
                  <div
                    className="check-box-checkout"
                    style={{ alignItems: "start" }}
                  >
                    <Checkbox
                      id="agree-all-checkbox"
                      type="checkbox"
                      defaultValue={user.agreeAll}
                      control={register("agreeAll", { required: true })}
                      aria-label="agree-all-checkbox"
                      className={`check-box-checkout ${
                        errors.agreeAll
                          ? "field floating-label-wrapper outline error-outline"
                          : ""
                      }`}
                      onChange={(val) => {
                        setValue("smsAgreed", val);
                        setValue("emailAgreed", val);
                        setIsAgreeAllChecked(val);
                      }}
                    />
                    <label
                      htmlFor="agree-all-checkbox"
                      className="agree-all-checkbox"
                    >
                      I agree to all
                    </label>
                  </div>
                </InputLayout>
              </form>
            </>
          ) : !standAlone ? (
            <div className="checkout-head">Express Checkout</div>
          ) : null}
          <div className="express-checkout-container">
            <div
              className={
                !isAgreeAllChecked && !displayButtonOnly
                  ? "disabled-stripe-payments"
                  : ""
              }
            >
              <PaymentRequestButtonElement options={options} />
            </div>
          </div>
          <div
            style={{
              display: !expressError ? "none" : "",
              color: "red",
              paddingTop: "8px",
              paddingBottom: "8px",
              fontSize: "14px",
            }}
          >
            {expressError}
          </div>
          {!!displayButtonOnly ? <Or /> : null}
        </div>
      </div>
    );
  }
};

export default ExpressCheckoutModal;
